/* eslint-disable jsx-a11y/label-has-associated-control */
import * as React from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { useLazyGetDialpadCallArtifactQuery } from '@/services/api/dialpadService';
import axiosInstance from '@/services/axiosConfig';

export const BPOOutcomePage: React.FC = () => {
  const [searchParams] = useSearchParams();

  const matterId = searchParams.get('matter_id') || '';
  // matter_name is embedded in the URL by the backend at call-initiation time
  // so this page works without an authenticated session.
  const matterName = searchParams.get('matter_name') || '';
  const pendingCallId = searchParams.get('pending_call_id') || '';
  const toNumber = searchParams.get('to_number') || '';
  const fromNumber = searchParams.get('from_number') || '';
  const workflowId = searchParams.get('workflow_id') || '';
  const runId = searchParams.get('run_id') || '';
  const coverageId = searchParams.get('coverage_id') || '';

  // callId may arrive immediately (Dialpad) or be resolved later via polling
  // once the RC webhook links the pending call to a telephony session.
  const [callId, setCallId] = React.useState(
    searchParams.get('call_id') || '',
  );

  const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);
  const [resolveArtifact] = useLazyGetDialpadCallArtifactQuery();

  // Poll for RC call ID once the webhook links the pending call.

  React.useEffect(() => {
    if (!pendingCallId || callId) return () => {};
    let cancelled = false;
    const poll = async () => {
      try {
        const resp = await axiosInstance.get(
          `/api/integrations/ringcentral/pending-calls/${pendingCallId}/`,
        );
        if (!cancelled && resp.data?.consumed) {
          if (resp.data?.rc_call_id) {
            setCallId(resp.data.rc_call_id as string);
          }
          // Stop polling once consumed, regardless of call_id presence
          clearInterval(interval);
        }
      } catch {
        // silently ignore transient errors
      }
    };
    poll();
    const interval = setInterval(poll, 3000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [pendingCallId, callId]);


  // Dynamic adjuster handles
  const [handles, setHandles] = React.useState<
    Array<{ type: 'PHONE' | 'EMAIL'; value: string }>
  >([{ type: 'PHONE', value: '' }]);

  const addHandle = () =>
    setHandles((prev) => [...prev, { type: 'PHONE', value: '' }]);
  const removeHandle = (idx: number) =>
    setHandles((prev) => prev.filter((_, i) => i !== idx));
  const updateHandle = (idx: number, field: 'type' | 'value', val: string) =>
    setHandles((prev) =>
      prev.map((h, i) =>
        i === idx
          ? { ...h, [field]: field === 'type' ? (val as any) : val }
          : h,
      ),
    );

  const handleOpenDialpadCall = async () => {
    if (!callId) return;
    try {
      const { data } = await resolveArtifact({ callId });
      // Prefer linking to artifacts page if we have artifact_id and matter_id
      if (data?.artifact_id && data?.matter_id) {
        window.open(
          `/matters/${data.matter_id}/artifacts/${data.artifact_id}`,
          '_blank',
        );
        return;
      }
      // Fall back to /call/ page if no artifact
      const convoId = data?.conversation_id;
      if (convoId) {
        window.open(`/call/${convoId}`, '_blank');
        return;
      }
      toast.error('No transcript available for this RingCentral call');
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Unknown error';
      toast.error('Failed to open RingCentral call', { description: message });
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    // Append dynamic handles as parallel arrays
    handles.forEach((h) => {
      if (h.value?.trim()) {
        formData.append('adjuster_handles_type[]', h.type);
        formData.append('adjuster_handles_value[]', h.value.trim());
      }
    });
    setIsSubmitting(true);
    try {
      const resp = await axiosInstance.post(
        '/api/integrations/dialpad/bpo-outcome',
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
        },
      );
      if (resp.status < 200 || resp.status >= 300) {
        throw new Error('Failed to save outcome');
      }
      toast.success('Outcome saved');
    } catch (err: any) {
      console.error(err);
      toast.error('Failed to save outcome', { description: err?.message });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex-1 w-full min-h-screen bg-gradient-to-b from-[#0b1020] to-[#0d1326] flex items-center justify-center p-6">
      <div className="w-full max-w-2xl rounded-xl border border-[#1f2a44] shadow-2xl bg-gradient-to-b from-[#121936] to-[#0f1731] overflow-hidden">
        <div className="px-6 py-5 border-b border-[#1f2a44] bg-[radial-gradient(1200px_300px_at_-10%_-20%,rgba(34,197,94,0.18),transparent_60%)]">
          <h2 className="text-lg font-semibold text-white">
            Log BPO Call Outcome
          </h2>
          <p className="text-xs text-[#9aa3b2] mt-1">
            Please record the result of your call. Only call outcome is
            required.
          </p>
        </div>
        <div className="p-6">

          <div className="mb-4 rounded-lg border border-[#1f2a44] p-3 text-sm text-[#9aa3b2] bg-[linear-gradient(180deg,rgba(255,255,255,0.02),rgba(255,255,255,0))]">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <span className="text-white font-medium">Matter</span>
                <div>{matterName || '—'}</div>
              </div>
              <div>
                <span className="text-white font-medium">Call ID</span>
                <div className="flex items-center gap-2">
                  <span>
                    {callId ||
                      (pendingCallId ? 'Waiting for call…' : '—')}
                  </span>
                  {callId ? (
                    <button
                      type="button"
                      onClick={handleOpenDialpadCall}
                      className="px-2 py-1 text-xs rounded border border-[#1f2a44] text-[#cfd6e4] hover:bg-[#182041]"
                      aria-label="Open RingCentral Call"
                    >
                      Open Call
                    </button>
                  ) : null}
                </div>
              </div>
              <div>
                <span className="text-white font-medium">To</span>
                <div>{toNumber || '—'}</div>
              </div>
              <div>
                <span className="text-white font-medium">From</span>
                <div>{fromNumber || '—'}</div>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label
                htmlFor="call_outcome"
                className="block text-xs text-[#9aa3b2] mb-1"
              >
                Call Outcome
              </label>
              <select
                id="call_outcome"
                name="call_outcome"
                required
                aria-label="Select the outcome of the call"
                className="w-full rounded-lg border border-[#1f2a44] bg-[#0a1228] text-white px-3 py-2 outline-none focus:ring-2 focus:ring-green-500"
              >
                <option value="">Select one</option>
                <option value="opened_claim">Opened Claim</option>
                <option value="got_claim_info">Got Claim Info</option>
                <option value="got_adjuster_contact">
                  Got Adjuster Name + Contact
                </option>
                <option value="not_successful">Call not successful</option>
              </select>
            </div>
            <div>
              <label
                htmlFor="claim_number"
                className="block text-xs text-[#9aa3b2] mb-1"
              >
                Claim Number
              </label>
              <input
                id="claim_number"
                name="claim_number"
                aria-label="Claim Number"
                className="w-full rounded-lg border border-[#1f2a44] bg-[#0a1228] text-white px-3 py-2 outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>
            {/* Benefit (applies to entire claim) */}
            <div>
              <label
                htmlFor="claim_benefit_type"
                className="block text-xs text-[#9aa3b2] mb-1"
              >
                Benefit
              </label>
              <select
                id="claim_benefit_type"
                name="claim_benefit_type"
                defaultValue="COMPREHENSIVE_AUTO"
                className="w-full rounded-lg border border-[#1f2a44] bg-[#0a1228] text-white px-3 py-2 outline-none focus:ring-2 focus:ring-green-500"
              >
                <option value="">(none)</option>
                <option value="PERSONAL_INJURY_PROTECTION">
                  Personal Injury Protection (PIP)
                </option>
                <option value="MEDICAL_PAYMENTS">
                  Medical Payments (MedPay)
                </option>
                <option value="UNINSURED_MOTORIST">
                  Uninsured Motorist (UM)
                </option>
                <option value="UNDER_INSURED_MOTORIST">
                  Underinsured Motorist (UIM)
                </option>
                <option value="BODILY_INJURY_LIABILITY">
                  Bodily Injury Liability (BI)
                </option>
                <option value="PROPERTY_DAMAGE_LIABILITY">
                  Property Damage Liability (PD)
                </option>
                <option value="COMPREHENSIVE_AUTO">Comprehensive (Auto)</option>
                <option value="COLLISION_AUTO">Collision (Auto)</option>
                <option value="OTHER">Other</option>
              </select>
            </div>
            <div>
              <label
                htmlFor="policy_number"
                className="block text-xs text-[#9aa3b2] mb-1"
              >
                Policy Identifier
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <select
                  id="policy_identifier_type"
                  name="policy_identifier_type"
                  defaultValue="POLICY_NUMBER"
                  className="w-full rounded-lg border border-[#1f2a44] bg-[#0a1228] text-white px-3 py-2 outline-none focus:ring-2 focus:ring-green-500"
                  aria-label="Policy Identifier Type"
                >
                  <option value="POLICY_NUMBER">Policy Number</option>
                  <option value="MEMBER_ID">Member ID</option>
                  <option value="GROUP_NUMBER">Group Number</option>
                  <option value="OTHER">Other</option>
                </select>
                <input
                  id="policy_number"
                  name="policy_number"
                  aria-label="Policy Identifier"
                  className="w-full rounded-lg border border-[#1f2a44] bg-[#0a1228] text-white px-3 py-2 outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>
            </div>
            <div>
              <label
                htmlFor="adjuster_name"
                className="block text-xs text-[#9aa3b2] mb-1"
              >
                Adjuster Name
              </label>
              <input
                id="adjuster_name"
                name="adjuster_name"
                aria-label="Adjuster Name"
                className="w-full rounded-lg border border-[#1f2a44] bg-[#0a1228] text-white px-3 py-2 outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>
            {/* Adjuster Handles (multiple) */}
            <div>
              <label className="block text-xs text-[#9aa3b2] mb-1">
                Adjuster Contacts
              </label>
              <div className="space-y-2">
                {handles.map((h, idx) => (
                  <div
                    key={idx}
                    className="grid grid-cols-12 gap-2 items-center"
                  >
                    <div className="col-span-4">
                      <select
                        aria-label="Handle type"
                        value={h.type}
                        onChange={(e) =>
                          updateHandle(idx, 'type', e.target.value)
                        }
                        className="w-full rounded-lg border border-[#1f2a44] bg-[#0a1228] text-white px-3 py-2 outline-none focus:ring-2 focus:ring-green-500"
                      >
                        <option value="PHONE">Phone</option>
                        <option value="EMAIL">Email</option>
                      </select>
                    </div>
                    <div className="col-span-7">
                      <input
                        aria-label="Handle value"
                        value={h.value}
                        onChange={(e) =>
                          updateHandle(idx, 'value', e.target.value)
                        }
                        placeholder={
                          h.type === 'PHONE'
                            ? '+1 (555) 555-5555'
                            : 'name@example.com'
                        }
                        className="w-full rounded-lg border border-[#1f2a44] bg-[#0a1228] text-white px-3 py-2 outline-none focus:ring-2 focus:ring-green-500"
                      />
                    </div>
                    <div className="col-span-1">
                      <button
                        type="button"
                        onClick={() => removeHandle(idx)}
                        className="px-2 py-2 text-xs rounded border border-[#1f2a44] text-[#cfd6e4] hover:bg-[#182041]"
                        aria-label="Remove contact"
                      >
                        −
                      </button>
                    </div>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addHandle}
                  className="px-3 py-2 text-xs rounded border border-[#1f2a44] text-[#cfd6e4] hover:bg-[#182041]"
                >
                  + Add Contact
                </button>
              </div>
            </div>
            <div>
              <label
                htmlFor="call_issues"
                className="block text-xs text-[#9aa3b2] mb-1"
              >
                Call Issues
              </label>
              <textarea
                id="call_issues"
                name="call_issues"
                aria-label="Call Issues"
                rows={3}
                placeholder="Note any issues that occurred during the call (optional)"
                className="w-full rounded-lg border border-[#1f2a44] bg-[#0a1228] text-white px-3 py-2 outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>
            <input type="hidden" name="workflow_id" value={workflowId} />
            <input type="hidden" name="run_id" value={runId} />
            <input type="hidden" name="matter_id" value={matterId} />
            <input type="hidden" name="call_id" value={callId} />
            <input type="hidden" name="pending_call_id" value={pendingCallId} />
            <input type="hidden" name="to_number" value={toNumber} />
            <input type="hidden" name="from_number" value={fromNumber} />
            <input type="hidden" name="coverage_id" value={coverageId} />
            {/* Origin marker for backend */}
            <input type="hidden" name="generated_by" value="human" />
            <div className="pt-2">
              <button
                type="submit"
                disabled={isSubmitting}
                className="inline-flex items-center justify-center rounded-lg bg-green-500 hover:bg-green-600 text-white font-semibold px-4 py-2 disabled:opacity-50"
                aria-label="Save Outcome"
              >
                {isSubmitting ? 'Saving…' : 'Save Outcome'}
              </button>
            </div>
            <p className="text-xs text-[#9aa3b2]">
              This will be added to the Voice AI run as a BPO action.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};

export default BPOOutcomePage;
